"use client";

import type { TRUSTED_HOME_LOCAL_PAGE_QUERY_RESULT } from "@homeinstead/schemas";

import { redirect, usePathname, useSearchParams } from "next/navigation";
import { Hero } from "~care/components/app/landing/Hero";
import CareGiverLeadForm from "~care/components/ui/LeadForm/CareGiverLeadForm/CareGiverLeadForm";
import CareSeekerLocalLeadForm from "~care/components/ui/LeadForm/CareSeekerShallowLeadForm/CareSeekerLocalLeadForm";
import { LeadFormDrawer } from "~care/components/ui/LeadForm/LeadFormDrawer";
import { useIsDebug } from "~care/hooks/useIsDebug";
import { useMultiStepForm } from "~care/hooks/useMultiStepForm";
import { logError } from "~care/lib/utils/log";
import { handleDrawerClose } from "~care/lib/utils/route-utils";

// Note that the displayed step and the actual step is off by 1 because the first step is hidden.
// Also, the final step is a Thank You page.
const INITIAL_STEP = 2;
const FINAL_STEP = 5;

export const LocalHero = ({
  className,
  pageData,
}: {
  className?: string;
  pageData: TRUSTED_HOME_LOCAL_PAGE_QUERY_RESULT;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Use the client search params for checking lead variant
  const careGiverForm = useMultiStepForm({
    finalStep: 2,
    initialStep: 2,
    onFirst: () => handleDrawerClose({ pathname, searchParams }),
  });

  const isCareGiverDrawerOpen = searchParams.get("leadVariant") === "CareGiver";

  const careSeekerShallowForm = useMultiStepForm({
    finalStep: FINAL_STEP,
    initialStep: INITIAL_STEP,
  });

  const { isDebug } = useIsDebug();

  if (!pageData) {
    logError({
      message: `No data found in Sanity for trusted home care local page with slug: ${searchParams.get("local")}.`,
    });

    // if there's no page data for the local page, there is probably still data for the national page.
    redirect("/ppc/trusted-home-care");
  }

  const {
    title,
    subtitle,
    franchise: { id, location, franchiseRating },
  } = pageData;

  return (
    <Hero className={className}>
      <CareSeekerLocalLeadForm
        className="xl:w-full"
        currentStep={careSeekerShallowForm.currentStep}
        finalStep={FINAL_STEP}
        franchiseId={Number(id)}
        franchiseRating={franchiseRating || undefined}
        handleBack={careSeekerShallowForm.prevStep}
        handleNext={careSeekerShallowForm.nextStep}
        isDebug={isDebug}
        leadSource={"PPC"}
        location={location}
        subtitle={subtitle}
        title={title}
      />
      <LeadFormDrawer
        description="Complete these steps to find care."
        hasBackButton
        isDrawerOpen={isCareGiverDrawerOpen} // Use careGiverForm's prevStep for the drawer's back button
        onBack={careGiverForm.prevStep}
        onClose={() => handleDrawerClose({ pathname, searchParams })} // This could also come from context/pageData if dynamic
        title="Get Care for a Loved One" // Ditto
      >
        {isCareGiverDrawerOpen && (
          <CareGiverLeadForm
            franchise={pageData.franchise}
            handleNext={careGiverForm.nextStep}
            isLastStep={careGiverForm.isLastStep}
          />
        )}
      </LeadFormDrawer>
    </Hero>
  );
};
